import consts from '../consts';

export const CAMERA_TYPES = {
  deteccao: {
    key: 'deteccao',
    title: 'Câmera de Detecção',
    subtitle: 'Acesse as câmeras de detecção cadastradas ou informe outro endereço IP.',
    icon: 'video',
  },
  monitoramento: {
    key: 'monitoramento',
    title: 'Câmera de Monitoramento',
    subtitle: 'Acesse as câmeras de monitoramento cadastradas ou informe outro endereço IP.',
    icon: 'eye',
  },
};

export function normalizeCameraUrl(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `http://${trimmed.replace(/^\/+/, '')}`;
}

const CAMERA_AUTH_STORAGE_KEY = 'ssat_camera_portal_auth';

export function cameraHostFromEndereco(value) {
  const url = normalizeCameraUrl(value);
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch (_) {
    return null;
  }
}

/** Páginas web de login (ex.: VigiaVL) — preview via iframe na gaveta. */
export function cameraUsaPortalWeb(value) {
  const url = normalizeCameraUrl(value);
  if (!url) return false;
  return /login\.jsp|\/login\b|vigia/i.test(url);
}

/**
 * VigiaVL / Pumatronix / túnel Varco: exigem HTTP Basic ou cookie de sessão.
 * Navegadores atuais não enviam Basic em &lt;img&gt; (sem user:pass@ na URL).
 * Nesses casos o MJPEG contínuo passa pelo backend SSAT, não pelo IP “cru” no browser.
 */
export function cameraStreamViaServidor(endereco) {
  const normalized = normalizeCameraUrl(endereco);
  if (!normalized) return false;
  if (cameraUsaPortalWeb(normalized)) return true;
  if (/tunnel\.varco\.cloud/i.test(normalized)) return true;
  try {
    const u = new URL(normalized);
    const path = u.pathname || '/';
    if (path === '/' && !cameraPareceStreamDireto(normalized)) return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

/** URL parece imagem ou MJPEG direto (sem portal). */
export function cameraPareceStreamDireto(value) {
  const url = normalizeCameraUrl(value);
  if (!url) return false;
  if (cameraUsaPortalWeb(url)) return false;
  return /\.(jpe?g|png|gif)(\?|$)|snapshot|mjpeg|videostream|image\.cgi|jpeg\.cgi|\/stream/i.test(url);
}

export function getCameraPortalAuth(host) {
  if (!host || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CAMERA_AUTH_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const entry = map[host];
    if (!entry || !entry.user) return null;
    return { user: entry.user, pass: entry.pass || '' };
  } catch (_) {
    return null;
  }
}

function subdomainFromBrowser() {
  if (typeof window === 'undefined') return 'localhost';
  const parts = window.location.hostname.split('.');
  return parts[0] === 'www' ? parts[1] : parts[0];
}

function buildCameraProxyQuery(cameraId, auth, apiToken, cacheBust) {
  const parts = [
    `token=${encodeURIComponent(apiToken)}`,
    `camera_user=${encodeURIComponent(auth.user)}`,
    `camera_pass=${encodeURIComponent(auth.pass || '')}`,
    `client=${encodeURIComponent(subdomainFromBrowser())}`,
  ];
  if (cacheBust != null && cacheBust !== '') {
    parts.push(`_=${encodeURIComponent(String(cacheBust))}`);
  }
  return parts.join('&');
}

/** Stream MJPEG contínuo via proxy SSAT (img src — sem polling). */
export function buildCameraMjpegStreamUrl(cameraId, auth, apiToken, cacheBust) {
  if (cameraId == null || !auth || !auth.user || !apiToken) return null;
  const base = (consts.API_URL || '').replace(/\/$/, '');
  return `${base}/cameras/${cameraId}/mjpeg?${buildCameraProxyQuery(cameraId, auth, apiToken, cacheBust)}`;
}

/** Snapshot JPEG via proxy (fallback quando o stream contínuo falha no navegador). */
export function buildCameraPreviewApiUrl(cameraId, auth, apiToken, cacheBust) {
  if (cameraId == null || !auth || !auth.user || !apiToken) return null;
  const base = (consts.API_URL || '').replace(/\/$/, '');
  return `${base}/cameras/${cameraId}/preview?${buildCameraProxyQuery(cameraId, auth, apiToken, cacheBust)}`;
}

export function clearCameraPortalAuth(host) {
  if (!host || typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(CAMERA_AUTH_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    delete map[host];
    localStorage.setItem(CAMERA_AUTH_STORAGE_KEY, JSON.stringify(map));
  } catch (_) {
    /* ignore */
  }
}

export function setCameraPortalAuth(host, user, pass) {
  if (!host || typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(CAMERA_AUTH_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[host] = { user: String(user || '').trim(), pass: pass || '' };
    localStorage.setItem(CAMERA_AUTH_STORAGE_KEY, JSON.stringify(map));
  } catch (_) {
    /* ignore */
  }
}

function appendAuthQuery(url, auth) {
  if (!auth || !auth.user) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has('user') && !u.searchParams.has('username')) {
      u.searchParams.set('user', auth.user);
      u.searchParams.set('password', auth.pass || '');
    }
    return u.toString();
  } catch (_) {
    return url;
  }
}

function withBasicAuthInUrl(urlString, auth) {
  if (!auth || !auth.user) return urlString;
  try {
    const u = new URL(urlString);
    u.username = auth.user;
    u.password = auth.pass || '';
    return u.toString();
  } catch (_) {
    return urlString;
  }
}

/** Navegador consegue carregar img http→http ou https→https; https→http é bloqueado (mixed content). */
export function browserPodeStreamDireto(urlString) {
  if (typeof window === 'undefined') return true;
  try {
    const u = new URL(urlString);
    if (window.location.protocol === 'https:' && u.protocol === 'http:') return false;
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * URLs MJPEG para o próprio navegador (sem passar pelo SSAT) — mesmo critério do backend.
 * Ordem: endereço cadastrado (se já for stream) → videostream.cgi → Pumatronix mjpegvideo.cgi → outros.
 */
function pushUnique(list, url) {
  if (url && !list.includes(url)) list.push(url);
}

export function buildDirectMjpegStreamCandidates(endereco, auth) {
  const normalized = normalizeCameraUrl(endereco);
  if (!normalized) return [];

  const urls = [];
  try {
    const u = new URL(normalized);
    const hasStreamPath =
      u.pathname && u.pathname !== '/' && !/\/login\.jsp$/i.test(u.pathname);
    if (hasStreamPath) {
      pushUnique(urls, appendAuthQuery(normalized, auth));
      pushUnique(urls, withBasicAuthInUrl(normalized, auth));
    }
  } catch (_) {
    return urls;
  }

  let origin;
  try {
    origin = new URL(normalized).origin;
  } catch (_) {
    return urls.filter(browserPodeStreamDireto);
  }

  const streamPaths = [
    `${origin}/videostream.cgi?rate=0`,
    `${origin}/api/mjpegvideo.cgi?InputNumber=1&StreamNumber=0`,
    `${origin}/api/mjpegvideo.cgi?InputNumber=1&StreamNumber=1`,
    `${origin}/GetJPEGStream.cgi`,
    `${origin}/cgi-bin/mjpg/video.cgi?channel=1&subtype=1`,
    `${origin}/video.mjpg`,
    `${origin}/mjpg/video.mjpg`,
  ];

  streamPaths.forEach(item => {
    // Query user/password primeiro — Chrome bloqueia user:pass@ em <img>
    pushUnique(urls, appendAuthQuery(item, auth));
    pushUnique(urls, withBasicAuthInUrl(item, auth));
    pushUnique(urls, withBasicAuthInUrl(appendAuthQuery(item, auth), auth));
  });

  return urls.filter(browserPodeStreamDireto);
}

export function appendCacheBustToUrl(urlString, cacheBust) {
  if (!urlString || cacheBust == null) return urlString;
  try {
    const u = new URL(urlString);
    u.searchParams.set('_', String(cacheBust));
    return u.toString();
  } catch (_) {
    const sep = urlString.includes('?') ? '&' : '?';
    return `${urlString}${sep}_=${cacheBust}`;
  }
}

export function buildCameraPreviewSrc(endereco, auth, cacheBust) {
  const candidates = buildDirectMjpegStreamCandidates(endereco, auth);
  const base = candidates[0] || normalizeCameraUrl(endereco);
  if (!base) return null;
  return appendCacheBustToUrl(base, cacheBust);
}

export function isValidCameraInput(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      return Boolean(url.hostname);
    } catch (_) {
      return false;
    }
  }

  const hostPattern = /^([\w.-]+|\d{1,3}(?:\.\d{1,3}){3})(:\d+)?(\/.*)?$/;
  return hostPattern.test(trimmed);
}
